import React, { FC, useCallback, useContext } from 'react';
import { ILiveStack, LiveStackStatusTransitional } from '../../hooks/useLiveApi';
import { createFetcher } from '../../contexts/Fetcher';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { PageHeader } from '../PageHeader/PageHeader';
import { LiveRegionsFetcher } from '../../fetchers/LiveRegionsFetcher';
import { LiveRegionSelect } from '../LiveRegionSelect/LiveRegionSelect';
import { useTranslation } from 'react-i18next';
import { LiveStack } from './LiveStack';
import { LiveRegionContext, LiveRegionContextProvider } from '../../contexts/LiveRegionContext';
import { useLoopedInterval } from '../../hooks/useLoopedInterval';
import { apiCloudLive } from '../../resources/api/apiCloudLive';
import { OrganizationContext } from '../../contexts/OrganizationContext';

const { useGetOrganizationStacksList, useGetOrganizationStacksListSilent } = apiCloudLive;

export const LivePreviewFetcher = createFetcher(() => {
  const { organization } = useContext(OrganizationContext);

  const { region } = useContext(LiveRegionContext);

  const { execute } = useGetOrganizationStacksList(organization ?? void 0, region);
  const { execute: executeSilent } = useGetOrganizationStacksListSilent(organization ?? void 0, region);

  return useCallback(
    async ({ silent = false }: { silent?: boolean }) => {
      if (!region) return [];
      return (silent ? executeSilent : execute)();
    },
    [execute, executeSilent, region]
  );
});

// Refresh once every 5 minutes when all stack are stable
const REFRESH_TIMEOUT_IDLE = 5 * 60 * 1000;
// Refresh once every 10 seconds when any stack is in transitional state
const REFRESH_TIMEOUT_TRANSITIONAL = 10 * 1000;

interface IProps {
  stacks: ILiveStack[];
  onRefresh: (props?: { silent?: boolean }) => Promise<any>;
}

export const LivePreviewContent: FC<IProps> = ({ stacks, onRefresh }) => {
  const { t } = useTranslation();
  const isAnyTransitionalState = (stacks ?? []).some((stack) => LiveStackStatusTransitional.includes(stack.status));

  const refreshTimeout = isAnyTransitionalState ? REFRESH_TIMEOUT_TRANSITIONAL : REFRESH_TIMEOUT_IDLE;

  useLoopedInterval({
    interval: refreshTimeout,
    callback: useCallback(() => onRefresh({ silent: true }), [onRefresh]),
  });

  if (!stacks) return null;

  return (
    <Grid container spacing={3}>
      {stacks.length === 0 ? (
        <Grid item xs={12}>
          <Typography paragraph>{t('live.preview.noChannels')}</Typography>
        </Grid>
      ) : null}
      {stacks.map((stack) => {
        return (
          <Grid item xs={12} md={6} lg={4} key={stack.stackName}>
            <LiveStack stack={stack} onRefresh={onRefresh} showUser={true} />
          </Grid>
        );
      })}
    </Grid>
  );
};

/*
const UploadAssets: FC = () => {
  const { t } = useTranslation();
  const { data } = useContext(LivePreviewFetcher.Context);

  const isUploadOpen = useQueryParam('action') === 'upload';
  const uploadAssetUrl = useQueryUrl('action', 'upload');
  const closeDialog = useCloseDialog(void 0, ['action']);

  if (!data || data.length === 0) return null;
  return (
    <>
      <Button component={Link} to={uploadAssetUrl} variant="contained" color="secondary" endIcon={<CloudUpload />}>
        {t('live.uploadAssetsButton')}
      </Button>
      <LiveUploadAssets open={isUploadOpen} closeDialog={closeDialog} />
    </>
  );
};
*/

const LivePreviewContentWithProps: FC = () => {
  const { data, onRefresh } = useContext(LivePreviewFetcher.Context);
  if (!data) return null;
  return <LivePreviewContent stacks={data} onRefresh={onRefresh} />;
};

export const CloudLive: FC = () => {
  const { t } = useTranslation();

  return (
    <LiveRegionContextProvider>
      <LivePreviewFetcher.WAF>
        <PageHeader title={t('live.preview.header')}>
          <LiveRegionsFetcher.WAF>
            <LiveRegionSelect />
          </LiveRegionsFetcher.WAF>
        </PageHeader>

        <LivePreviewContentWithProps />
      </LivePreviewFetcher.WAF>
    </LiveRegionContextProvider>
  );
};
